/**
 * Poll /channels/now on schedule boundaries + visibility regain.
 * Keeps Watch Live aligned with published schedule without redesigning the player.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchChannelNow } from "./channelScheduleApi";
import { nextChannelNowPollMs } from "./channelPlaybackHelpers";

/**
 * @param {string} channelKey
 * @param {{ enabled?: boolean, onPayload?: function }} [options]
 */
export function useChannelNow(channelKey, options) {
  const enabled = !options || options.enabled !== false;
  const onPayload = options && options.onPayload;
  const [nowPayload, setNowPayload] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(enabled && channelKey));
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const onPayloadRef = useRef(onPayload);
  onPayloadRef.current = onPayload;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const applyPayload = useCallback((payload) => {
    setNowPayload(payload);
    if (typeof onPayloadRef.current === "function") {
      onPayloadRef.current(payload);
    }
  }, []);

  const refresh = useCallback(
    async (opts) => {
      if (!channelKey || !enabled) return null;
      const silent = opts && opts.silent;
      if (!silent) setLoading(true);
      try {
        const payload = await fetchChannelNow({
          slug: channelKey,
          unique_id: channelKey,
          channel_id: channelKey,
        });
        if (cancelledRef.current) return null;
        if (!payload || payload.success === false) {
          setError((payload && (payload.error || payload.error_messages)) || "Channel not found");
          applyPayload(null);
          return null;
        }
        setError("");
        applyPayload(payload);
        return payload;
      } catch (e) {
        if (!cancelledRef.current) {
          setError((e && e.message) || "Failed to load channel now-playing");
        }
        return null;
      } finally {
        if (!cancelledRef.current && !silent) setLoading(false);
      }
    },
    [channelKey, enabled, applyPayload]
  );

  useEffect(() => {
    cancelledRef.current = false;
    clearTimer();
    if (!enabled || !channelKey) {
      setLoading(false);
      return undefined;
    }

    var scheduleNext = function (payload) {
      clearTimer();
      var delay = nextChannelNowPollMs(payload);
      timerRef.current = setTimeout(async function () {
        var next = await refresh({ silent: true });
        if (!cancelledRef.current) scheduleNext(next);
      }, delay);
    };

    (async function () {
      var payload = await refresh({ silent: false });
      if (!cancelledRef.current) scheduleNext(payload);
    })();

    var onVisibility = function () {
      if (document.visibilityState === "visible") {
        refresh({ silent: true }).then(function (payload) {
          if (!cancelledRef.current) scheduleNext(payload);
        });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return function () {
      cancelledRef.current = true;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [channelKey, enabled, refresh, clearTimer]);

  return {
    nowPayload,
    error,
    loading,
    refresh,
    setNowPayload: applyPayload,
  };
}

export default useChannelNow;
